"use client";

import { MonacoSDK } from "@0xmonaco/core";
import { type ConnectedWallet } from "@privy-io/react-auth";
import { createContext, useContext, useEffect, useState, useRef, type ReactNode } from "react";
import { createWalletClient, custom, http } from "viem";
import { seiTestnet } from "viem/chains";

import {
  clearMonacoTokens,
  getMonacoRefreshToken,
  setMonacoTokens,
} from "./monaco-token-refresh";
import { getEnv } from "../../../platform/env/env";

export type MonacoAuthStatus =
  | "idle"
  | "initializing"
  | "restoring"
  | "authenticating"
  | "authenticated"
  | "error";

interface MonacoSDKContextType {
  sdk: MonacoSDK | null;
  isLoading: boolean;
  isConnected: boolean;
  isAuthenticated: boolean;
  authStatus: MonacoAuthStatus;
  authError: string | null;
}

const MonacoSDKContext = createContext<MonacoSDKContextType>({
  sdk: null,
  isLoading: true,
  isConnected: false,
  isAuthenticated: false,
  authStatus: "idle",
  authError: null,
});

/**
 * Hook to access the Monaco SDK.
 * Must be used inside MonacoSDKProvider.
 */
export const useMonacoSDKContext = () => useContext(MonacoSDKContext);

/**
 * Props for MonacoSDKProvider - receives Privy data from parent
 * to avoid calling Privy hooks directly (fixes context issues in production builds)
 */
export interface MonacoSDKProviderProps {
  children: ReactNode;
  privyReady: boolean;
  authenticated: boolean;
  user: unknown;
  wallets: ConnectedWallet[];
  walletsReady: boolean;
}

/**
 * Creates a read-only Monaco SDK instance that can query public endpoints
 * without requiring a wallet or authentication.
 */
function createPublicSDK(): MonacoSDK {
  const dummyAddress = "0x0000000000000000000000000000000000000000" as `0x${string}`;
  const walletClient = createWalletClient({
    account: dummyAddress,
    transport: http(getEnv.VITE_SEI_RPC_URL as string),
    chain: seiTestnet,
  });

  return new MonacoSDK({
    walletClient,
    network: getEnv.VITE_MONACO_NETWORK as string,
    seiRpcUrl: getEnv.VITE_SEI_RPC_URL as string,
    wsUrl: getEnv.VITE_SEI_RPC_WS as string,
  });
}

/**
 * Provider component that initializes and manages the Monaco SDK.
 * 
 * The SDK is initialized in two phases:
 * 1. Public SDK: Created immediately so public endpoints (pairs, orderbook, trades, candles) work
 * 2. Authenticated SDK: Created when a Privy wallet is available, replacing the public SDK
 * 
 * If authentication fails, the public SDK remains available so market data still displays.
 */
export function MonacoSDKProvider({ 
  children,
  privyReady,
  authenticated,
  user,
  wallets,
  walletsReady,
}: MonacoSDKProviderProps) {
  const [sdk, setSdk] = useState<MonacoSDK | null>(null);
  const [authStatus, setAuthStatus] = useState<MonacoAuthStatus>("idle");
  const [authError, setAuthError] = useState<string | null>(null);
  
  const isInitializingRef = useRef(false);
  const initializedWalletRef = useRef<string | null>(null);
  const sdkRef = useRef<MonacoSDK | null>(null);
  const publicSdkRef = useRef<MonacoSDK | null>(null);

  const ready = privyReady && walletsReady;
  
  const embeddedWallet = wallets.find(
    (wallet) => wallet.walletClientType === "privy",
  );
  const walletAddress = embeddedWallet?.address ?? null;

  const cleanupSDK = (sdkInstance: MonacoSDK | null) => {
    if (sdkInstance) {
      try {
        sdkInstance.ws.disconnect();
      } catch {
        // Ignore cleanup errors
      }
    }
  };

  // Phase 1: Create public SDK immediately so public endpoints work
  useEffect(() => {
    if (publicSdkRef.current) return;

    try {
      const publicSdk = createPublicSDK();
      publicSdkRef.current = publicSdk;
      // Only set as active SDK if we don't already have an authenticated one
      if (!sdkRef.current) {
        setSdk(publicSdk);
      }
    } catch {
      // Public SDK creation failed - this shouldn't happen
    }
  }, []);

  // Phase 2: Authenticate when wallet is available, replacing the public SDK
  useEffect(() => {
    if (authStatus === "authenticated" && initializedWalletRef.current === walletAddress) {
      return;
    }

    if (isInitializingRef.current) {
      return;
    }

    const initializeAndLogin = async () => {
      if (!ready) {
        if (authStatus !== "idle" && authStatus !== "error") {
          // Wallet disconnected - fall back to public SDK
          cleanupSDK(sdkRef.current);
          sdkRef.current = null;
          if (publicSdkRef.current) {
            setSdk(publicSdkRef.current);
          }
          setAuthStatus("idle");
          setAuthError(null);
          initializedWalletRef.current = null;
        }
        return;
      }

      if (!wallets.length || !embeddedWallet) {
        // No wallet - keep public SDK active
        return;
      }

      isInitializingRef.current = true;
      setAuthStatus("initializing");
      setAuthError(null);

      try {
        cleanupSDK(sdkRef.current);
        sdkRef.current = null;

        await embeddedWallet.switchChain(1328);
        const provider = await embeddedWallet.getEthereumProvider();
        const account = embeddedWallet.address as `0x${string}`;

        const walletClient = createWalletClient({
          account,
          transport: custom(provider),
          chain: seiTestnet,
        });

        const monacoSDK = new MonacoSDK({
          walletClient: walletClient,
          network: getEnv.VITE_MONACO_NETWORK as string,
          seiRpcUrl: getEnv.VITE_SEI_RPC_URL as string,
          wsUrl: getEnv.VITE_SEI_RPC_WS as string,
        });

        const storedRefreshToken = getMonacoRefreshToken();

        if (storedRefreshToken) {
          setAuthStatus("restoring");
          try {
            const newTokens = await monacoSDK.auth.refreshToken(storedRefreshToken);
            setMonacoTokens(newTokens.accessToken, storedRefreshToken);
            propagateMonacoAccessToken(monacoSDK, newTokens.accessToken);

            sdkRef.current = monacoSDK;
            setSdk(monacoSDK);
            setAuthStatus("authenticated");
            initializedWalletRef.current = embeddedWallet.address;
            isInitializingRef.current = false;
            return;
          } catch {
            clearMonacoTokens();
          }
        }

        setAuthStatus("authenticating");
        const monacoClientId = getEnv.VITE_MONACO_CLIENT_ID;
        if (!monacoClientId) {
          throw new Error("VITE_MONACO_CLIENT_ID is not set");
        }

        const authState = await monacoSDK.login(monacoClientId);
        setMonacoTokens(authState.accessToken, authState.refreshToken);

        propagateMonacoAccessToken(monacoSDK, authState.accessToken);
        sdkRef.current = monacoSDK;
        setSdk(monacoSDK);
        setAuthStatus("authenticated");
        setAuthError(null);
        initializedWalletRef.current = embeddedWallet.address;
      } catch (err) {
        cleanupSDK(sdkRef.current);
        sdkRef.current = null;
        // Fall back to public SDK so market data still works
        if (publicSdkRef.current) {
          setSdk(publicSdkRef.current);
        }
        setAuthStatus("error");
        setAuthError(
          err instanceof Error
            ? err.message
            : "Your account is not authorized for Monaco beta testing",
        );
        initializedWalletRef.current = null;
        clearMonacoTokens();
      } finally {
        isInitializingRef.current = false;
      }
    };

    initializeAndLogin();

    return () => {
      cleanupSDK(sdkRef.current);
    };
  }, [ready, walletAddress]);

  // Cleanup public SDK on unmount
  useEffect(() => {
    return () => {
      cleanupSDK(publicSdkRef.current);
    };
  }, []);

  const isLoading =
    authStatus === "initializing" ||
    authStatus === "restoring" ||
    authStatus === "authenticating";

  const contextValue: MonacoSDKContextType = {
    sdk,
    isLoading,
    isConnected: ready && wallets.length > 0,
    isAuthenticated: authStatus === "authenticated",
    authStatus,
    authError,
  };

  return (
    <MonacoSDKContext.Provider value={contextValue}>
      {children}
    </MonacoSDKContext.Provider>
  );
}

export const propagateMonacoAccessToken = (monacoSDK: MonacoSDK, token: string) => {
  monacoSDK.auth.setAccessToken(token);
  monacoSDK.applications.setAccessToken(token);
  monacoSDK.fees.setAccessToken(token);
  monacoSDK.vault.setAccessToken(token);
  monacoSDK.trading.setAccessToken(token);
  monacoSDK.market.setAccessToken(token);
  monacoSDK.profile.setAccessToken(token);
  monacoSDK.ws.setToken(token);
};
